export type TimezoneOption = { id: string; label: string; description: string };

// Single source of truth for the app's preferred display timezone (mirrors lib/locale.ts).
// Precedence: profile "Timezone" preference (see LocalePreferenceSync in app/providers.tsx) >
// browser's IANA timezone > 'UTC'.
let preferredTimezoneOverride: string | null = null;

/** Sets the user's explicit timezone preference (from their profile); pass null/undefined to clear it. */
export function setPreferredTimezone(timezone: string | null | undefined): void {
  preferredTimezoneOverride = timezone || null;
}

/** Resolves the IANA timezone to use for date/time formatting: profile override > browser timezone > 'UTC'. */
export function getPreferredTimezone(): string {
  if (preferredTimezoneOverride) return preferredTimezoneOverride;
  return getBrowserTimezone();
}

/** The browser's own IANA timezone, ignoring any profile override - falls back to 'UTC'. */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { id: 'UTC', label: 'UTC (Coordinated Universal Time)', description: 'UTC' },
  { id: 'America/New_York', label: 'Eastern Time (US & Canada)', description: 'America/New_York' },
  { id: 'America/Chicago', label: 'Central Time (US & Canada)', description: 'America/Chicago' },
  { id: 'America/Denver', label: 'Mountain Time (US & Canada)', description: 'America/Denver' },
  { id: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)', description: 'America/Los_Angeles' },
  { id: 'America/Anchorage', label: 'Alaska Time', description: 'America/Anchorage' },
  { id: 'Pacific/Honolulu', label: 'Hawaii Time', description: 'Pacific/Honolulu' },
  { id: 'Europe/London', label: 'London (GMT/BST)', description: 'Europe/London' },
  { id: 'Europe/Paris', label: 'Central European Time', description: 'Europe/Paris' },
  { id: 'Europe/Berlin', label: 'Berlin', description: 'Europe/Berlin' },
  { id: 'Asia/Dubai', label: 'Dubai (UTC+4)', description: 'Asia/Dubai' },
  { id: 'Asia/Kolkata', label: 'India Standard Time', description: 'Asia/Kolkata' },
  { id: 'Asia/Tokyo', label: 'Tokyo', description: 'Asia/Tokyo' },
  { id: 'Asia/Shanghai', label: 'China Standard Time', description: 'Asia/Shanghai' },
  { id: 'Asia/Singapore', label: 'Singapore', description: 'Asia/Singapore' },
  { id: 'Australia/Sydney', label: 'Sydney', description: 'Australia/Sydney' },
  { id: 'Pacific/Auckland', label: 'Auckland', description: 'Pacific/Auckland' },
];
